import { MAP_DEFAULT_CENTER } from '../../config';
import { routingJobsApi, RoutingJobResponse, RoutingJobViewResponse } from '../../api/routingJobsApi';
import {
  TaskConfig,
  TaskInfo,
  Trip,
  TouchPoint,
  EditPerformance,
  BreachesAndRatios,
  TripCostPerformance,
  TripMeta,
} from '../../models/types';
import { OrderBundle, VerifyPayload, DriverInfo, VehicleInfo } from '../../api/wmService';

type AnyRecord = Record<string, any>;
const GEO_CORRECTED_TAG = '##geo-location-corrected';
const LOW_CONFIDENCE_GEO_TAG = '##low_confidence_geo_loc##';
const DERIVED_GEO_TAG = '##derived_geo_loc##';

const DEFAULT_EDIT_PERFORMANCE: EditPerformance = {
  editPercent: 0,
  tpPerTrip: 0,
  wtPerTripKg: 0,
  distPerTripKm: 0,
  tpsWithoutTrip: 0,
  noOfTrips: 0,
  cpk: 0,
  cpTp: 0,
  tripsWithTimeBreach: 0,
  noOfSlotSlippage: 0,
  tpPerTripDelta: 0,
  wtPerTripKgDelta: 0,
  distPerTripKmDelta: 0,
  tpsWithoutTripDelta: 0,
  noOfTripsDelta: 0,
  cpkDelta: 0,
  cpTpDelta: 0,
  tripsWithTimeBreachDelta: 0,
  noOfSlotSlippageDelta: 0,
  ctoGmv: 0,
  ctoGmvDelta: 0,
};

export function adaptJumboBuyTaskInfo(
  depotId: string,
  jobId: string,
  summaryView: RoutingJobViewResponse,
  tripsView: RoutingJobViewResponse
): TaskInfo {
  const summary = summaryView.summary ?? {};
  const editPerformance = normalizeEditPerformance(summary.editPerformance);
  const trips = (tripsView.items ?? []).map(mapTrip);
  const config = buildTaskConfig(depotId, summary, trips);

  return {
    taskId: jobId,
    userId: summary.createdBy ?? '',
    tenant: summary.tenantId ?? 'jumbobuy',
    status: summary.status ?? 'COMPLETED',
    config,
    editPerf: editPerformance,
    trips,
  };
}

export async function fetchJumboBuyVerifyPayload(depotId: string, jobId: string): Promise<VerifyPayload> {
  const workspace = await routingJobsApi.getVerifyWorkspace(depotId, jobId);
  const views = Object.fromEntries((workspace.views ?? []).map((view) => [view.viewId, view]));
  return adaptJumboBuyVerifyPayload(workspace.job, views);
}

export function adaptJumboBuyVerifyPayload(
  job: RoutingJobResponse,
  views: Record<string, RoutingJobViewResponse | undefined>
): VerifyPayload {
  const buyersView = views.verify_buyers;
  const driversView = views.verify_drivers;
  const summaryView = views.edit_summary;
  const buyers = buyersView?.items ?? [];
  const drivers = driversView?.items ?? [];
  const sourceLocation = summaryView?.summary?.settings?.sourceLocation;
  const defaultLat = buyers.length ? toNumber(buyers[0].dropLatitude) : MAP_DEFAULT_CENTER[1];
  const defaultLng = buyers.length ? toNumber(buyers[0].dropLongitude) : MAP_DEFAULT_CENTER[0];
  const originLat = Array.isArray(sourceLocation) && sourceLocation.length === 2
    ? Number(sourceLocation[1])
    : defaultLat;
  const originLng = Array.isArray(sourceLocation) && sourceLocation.length === 2
    ? Number(sourceLocation[0])
    : defaultLng;
  const originName = job.depotSnapshot?.name || 'Depot';
  const originId = job.depotId || job.id;
  const pickupId = `${originId}-DEPOT`;

  const dropLookups: Record<string, { dropId: string; address: string; lat: number; lng: number; hourStart: string; hourEnd: string }> = {};
  const orders: OrderBundle[] = buyers.map((buyer: AnyRecord, index: number) => {
    const soId = buyer.orgUnitId || `BUY-${index + 1}`;
    const lat = toNumber(buyer.dropLatitude) || originLat;
    const lng = toNumber(buyer.dropLongitude) || originLng;
    const rawAddress = buyer.address || buyer.buyerName || '';
    const geoStatus = resolveGeoStatus(rawAddress);
    const geoCorrected = geoStatus === 'corrected';
    const address = stripGeoCorrectedTag(rawAddress);
    dropLookups[buyer.orgUnitId ?? soId] = {
      dropId: buyer.orgUnitId ?? soId,
      address,
      lat,
      lng,
      hourStart: buyer.timeWindowStart || '06:00',
      hourEnd: buyer.timeWindowEnd || '10:00',
    };
    return {
      soId,
      buyerName: buyer.buyerName || soId,
      pinCode: buyer.pinCode || '',
      geoCorrected,
      geoStatus,
      fromTime: buyer.timeWindowStart || '',
      toTime: buyer.timeWindowEnd || '',
      wasteType: buyer.itemsSummaryItemTypes || '',
      qty: toNumber(buyer.dropWeightKg),
      uom: 'kg',
      hazMat: 'No',
      requiredAsset: '',
      pickup: {
        id: pickupId,
        lat: originLat,
        lng: originLng,
        address: originName,
      },
      drop: {
        id: buyer.orgUnitId ?? soId,
        lat,
        lng,
        address,
      },
      pickupGeoCorrection: {
        ref: pickupId,
        role: 'ORIGIN',
        editable: false,
      },
      dropGeoCorrection: {
        ref: buyer.orgUnitId ?? soId,
        role: 'DROP',
        editable: true,
      },
    };
  });

  const verifyDrivers: DriverInfo[] = drivers.map(mapVerifyDriverToInfo);
  const fleet: VehicleInfo[] = drivers.map(mapVerifyDriverToFleet);

  return {
    runId: job.id,
    origin: {
      originId,
      name: originName,
      lat: originLat,
      lng: originLng,
      zip: job.depotSnapshot?.configFilesMap?.pinCode ?? '',
    },
    orders,
    drivers: verifyDrivers,
    fleet,
    equipment: [],
    lookups: {
      origin: {
        originId,
        address: originName,
        zip: job.depotSnapshot?.configFilesMap?.pinCode ?? '',
        lat: originLat,
        lng: originLng,
        hourStart: '06:00',
        hourEnd: '22:00',
      },
      clients: {
        [pickupId]: {
          clientId: pickupId,
          address: originName,
          lat: originLat,
          lng: originLng,
          hourStart: '06:00',
          hourEnd: '22:00',
        },
      },
      drops: dropLookups,
    },
  };
}

export function mapJumboBuyOrders(): OrderBundle[] {
  // JumboBuy edit flow does not rely on verify orders payload yet.
  return [];
}

function mapTrip(item: AnyRecord): Trip {
  const geoMarkers = item.geoMarkers ?? {};
  const tpDetails = Array.isArray(item.buyers) ? item.buyers.map(mapBuyerTouchPoint) : [];
  const breaches = fillBreaches(item.breachesAndRatios ?? item);
  const tripCostPerf = fillCostPerformance(item);
  const slotTags = normalizeSlotTags(item.slots ?? item.tripSlots);
  const name = String(item.tripName ?? item.tripId ?? item.vehicleNumber ?? 'Trip');
  const tripMeta = buildTripMeta(item);
  const availableCapacity = normalizeAvailableCapacity(item.availableCapacity ?? item);
  const totalWeightKg = toNumber(item.weightKg ?? item.tripWeightKg);
  const capacityKg =
    availableCapacity.avlKgs > 0
      ? totalWeightKg + availableCapacity.avlKgs
      : undefined;
  const travelMinutes = toNumber(item.travelMinutes ?? item.tripTravelMinutes ?? parseTravelMinutes(item.travelTime));
  const originStartMinutes = timeToMinutes(item.tripStartTime);

  return {
    name,
    vehTripNo: Number(item.tripId ?? 0),
    distKm: toNumber(item.distKm),
    totalWtKg: totalWeightKg,
    volCuFt: ccToCuFt(item.volCc),
    slotTags,
    geoMarkers: {
      tripBoundaryLngLats: geoMarkers.tripBoundaryLngLats ?? [],
      tripCenterLngLat: geoMarkers.tripCenterLngLat ?? MAP_DEFAULT_CENTER,
      tripPolylineLngLats: geoMarkers.tripPolylineLngLats,
    },
    breachesAndRatios: breaches,
    tripCostPerf,
    vehDetails: {
      vehId: item.vehicleId ?? item.vehicleNumber ?? '',
      driverId: item.driverId ?? '',
      driverName: item.driverName ?? '',
      contractId: item.contractId ?? '',
      contractType: item.contractType ?? '',
      capacityKg,
    },
    tpDetails,
    sortValues: normalizeSortValues(item.sortValues),
    tripMeta,
    assignmentMeta: {
      tripDurationMinutes: travelMinutes > 0 ? travelMinutes : undefined,
      originStartMinutes: originStartMinutes > 0 ? originStartMinutes : undefined,
    },
  };
}

function mapBuyerTouchPoint(buyer: AnyRecord): TouchPoint {
  const lngLat = Array.isArray(buyer.lngLat) ? buyer.lngLat : [buyer.dropLongitude, buyer.dropLatitude];
  const attempt = toAttemptTime(buyer.deliveredAt, buyer.deliveryEndsAt);
  const slotTags = buyer.slotTag ? [buyer.slotTag] : [];
  const itemSummary = buyer.itemsSummary ?? {};
  const rawAddress = String(buyer.address ?? '');
  const geoStatus = resolveGeoStatus(rawAddress);
  const geoCorrected = geoStatus === 'corrected';

  return {
    tpId: String(buyer.orgUnitId ?? ''),
    routeGeometryNodeKey: buyer.routeGeometryNodeKey
      ? String(buyer.routeGeometryNodeKey)
      : undefined,
    seqNo: Number(buyer.sequence ?? 0),
    name: String(buyer.buyerName ?? buyer.orgUnitId ?? ''),
    address: stripGeoCorrectedTag(rawAddress),
    zipCode: buyer.pinCode ?? '',
    lngLat: [toNumber(lngLat?.[0]), toNumber(lngLat?.[1])],
    attemptTime: attempt,
    openHoursMinOfDay: buildOpenHours(buyer.timeWindowStart, buyer.timeWindowEnd),
    slotTags,
    timeBreachMin: toNumber(buyer.timeBreachMinutes),
    itemSummary: {
      dropWtKg: toNumber(buyer.dropWeightKg),
      pickWtKg: toNumber(itemSummary.pickWeightKg),
      volCuFt: ccToCuFt(buyer.dropVolumeCc),
      gmv: toNumber(buyer.gmvInRs),
      dropItemCount: toNumber(itemSummary.dropItemCount),
      pickItemCount: toNumber(itemSummary.pickItemCount),
      distinctItemTags: itemSummary.itemTypes ?? buyer.itemTypes ?? [],
    },
    orderNumber: buyer.orgUnitId ?? '',
    customerName: buyer.buyerName,
    generatorName: buyer.clusterId,
    scheduledDateTime: buyer.timeWindowStart,
    deliveryDurationMinutes: toNumber(buyer.transactionTime),
    timeWindowRaw: [buyer.timeWindowStart, buyer.timeWindowEnd].filter(Boolean).join(' - ') || undefined,
    geoCorrected,
    geoStatus,
    geoCorrectionRef: buyer.orgUnitId ? String(buyer.orgUnitId) : undefined,
    geoCorrectionRole: 'DROP',
    geoCorrectionSupported: true,
    extra: {
      toteCountTag: buyer.toteCountTag,
      floorNumber: buyer.floorNumber,
      clusterId: buyer.clusterId,
      transactionTime: buyer.transactionTime,
      slotTag: buyer.slotTag,
    },
  };
}

function buildTripMeta(item: AnyRecord): TripMeta {
  const breaches = fillBreaches(item.breachesAndRatios ?? item);
  const availableCapacity = normalizeAvailableCapacity(item.availableCapacity ?? item);
  return {
    status: 'PLANNED',
    loadId: String(item.tripId ?? ''),
    assignmentCount: toNumber(item.assignmentCount),
    extra: {
      availableCapacity,
      travelTime: item.travelTime,
      gmv: toNumber(item.gmvInRs ?? item.tripGmvInRs),
      tripStartTime: item.tripStartTime,
      tripEndTime: item.tripEndTime,
      zoneId: item.zoneId,
      driverId: item.driverId,
      pinCodes: Array.isArray(breaches.distinctZipCodes) ? breaches.distinctZipCodes : [],
    },
  };
}

function buildTaskConfig(depotId: string, summary: AnyRecord, trips: Trip[]): TaskConfig {
  const sourceLocation = summary.settings?.sourceLocation;
  const center: [number, number] = Array.isArray(sourceLocation) && sourceLocation.length === 2
    ? [Number(sourceLocation[0]), Number(sourceLocation[1])]
    : findMapCenter(trips);
  return {
    id: depotId,
    algoType: summary.modelType ?? 'ROUTING',
    sourceLngLat: center,
    sourceRouteGeometryNodeKey: summary.settings?.sourceRouteGeometryNodeKey
      ? String(summary.settings.sourceRouteGeometryNodeKey)
      : undefined,
    geoBarriers: [],
  };
}

function findMapCenter(trips: Trip[]): [number, number] {
  for (const trip of trips) {
    const center = trip.geoMarkers?.tripCenterLngLat;
    if (Array.isArray(center) && center.length === 2) {
      return [center[0], center[1]];
    }
  }
  return MAP_DEFAULT_CENTER;
}

function normalizeEditPerformance(raw: AnyRecord | undefined): EditPerformance {
  if (!raw) {
    return DEFAULT_EDIT_PERFORMANCE;
  }
  const units = typeof raw.units === 'object' ? raw.units : undefined;
  return {
    editPercent: toNumber(raw.editPercent),
    tpPerTrip: toNumber(raw.tpPerTrip),
    wtPerTripKg: toNumber(raw.kgsPerTrip ?? raw.wtPerTripKg),
    distPerTripKm: toNumber(raw.kmsPerTrip ?? raw.distPerTripKm),
    tpsWithoutTrip: toNumber(raw.tpsWithoutTrip),
    noOfTrips: toNumber(raw.tripCount ?? raw.noOfTrips),
    cpk: toNumber(raw.cpk),
    cpTp: toNumber(raw.cpt ?? raw.cpTp),
    tripsWithTimeBreach: toNumber(raw.timeBreach ?? raw.tripsWithTimeBreach),
    noOfSlotSlippage: toNumber(raw.slotSlippages ?? raw.noOfSlotSlippage),
    tpPerTripDelta: toNumber(raw.tpPerTripDelta),
    wtPerTripKgDelta: toNumber(raw.kgsPerTripDelta ?? raw.wtPerTripKgDelta),
    distPerTripKmDelta: toNumber(raw.kmsPerTripDelta ?? raw.distPerTripKmDelta),
    tpsWithoutTripDelta: toNumber(raw.tpsWithoutTripDelta),
    noOfTripsDelta: toNumber(raw.tripCountDelta ?? raw.noOfTripsDelta),
    cpkDelta: toNumber(raw.cpkDelta),
    cpTpDelta: toNumber(raw.cptDelta ?? raw.cpTpDelta),
    tripsWithTimeBreachDelta: toNumber(raw.timeBreachDelta ?? raw.tripsWithTimeBreachDelta),
    noOfSlotSlippageDelta: toNumber(raw.slotSlippagesDelta ?? raw.noOfSlotSlippageDelta),
    ctoGmv: toNumber(raw.costAsPctOfGmv ?? raw.ctoGmv),
    ctoGmvDelta: toNumber(raw.costAsPctOfGmvDelta ?? raw.ctoGmvDelta),
    units,
  };
}

function fillBreaches(raw: AnyRecord | undefined): BreachesAndRatios {
  const source = raw ?? {};
  return {
    wtBreachKg: toNumber(source.wtBreachKg ?? source.tripBreachWtKg),
    volBreachCuFt: ccToCuFt(source.volBreachCc ?? source.tripBreachVolCc),
    distBreachKm: toNumber(source.distBreachKm ?? source.tripBreachDistKm),
    tpBreach: toNumber(source.tpBreach ?? source.tripBreachPoints),
    timeBreachMin: toNumber(source.timeBreachMin ?? source.tripBreachTravelTime),
    slotBreaches: toNumber(source.slotBreaches ?? source.tripBreachSlot),
    wtToLimit: toNumber(source.wtToLimitRatio ?? source.tripBreachWtToLimitRatio),
    volToLimit: toNumber(source.volToLimitRatio ?? source.tripBreachVolToLimitRatio),
    distToLimit: toNumber(source.distToLimitRatio ?? source.tripBreachDistToLimitRatio),
    tpToLimit: toNumber(source.tpToLimitRatio ?? source.tripBreachPointsToLimitRatio),
    timeToLimit: toNumber(source.timeToLimitRatio ?? source.tripBreachTimeToLimitRatio),
    distinctZipCodes: normalizeStringArray(source.pinCodes ?? source.tripBreachPinCodes),
  };
}

function fillCostPerformance(item: AnyRecord): TripCostPerformance {
  return {
    cost: toNumber(item.tripCostInRs),
    cpk: toNumber(item.tripCpk),
    cpt: toNumber(item.tripCptRs),
    gmv: toNumber(item.gmvInRs),
    ctoGmv: toNumber(item.tripCostAsPctOfGmv) / 100,
  };
}

function normalizeSortValues(raw: AnyRecord | undefined): Record<string, number | string> | undefined {
  if (!raw) return undefined;
  const normalized: Record<string, number | string> = {};
  Object.entries(raw).forEach(([key, value]) => {
    if (typeof value === 'number') {
      normalized[key] = value;
      return;
    }
    if (typeof value === 'string') {
      const parsed = Number(value);
      normalized[key] = Number.isFinite(parsed) ? parsed : value;
      return;
    }
    if (typeof value === 'boolean') {
      normalized[key] = value ? 1 : 0;
    }
  });
  return normalized;
}

function toAttemptTime(start?: string, end?: string): { start: number; end: number } {
  return {
    start: timeToMinutes(start),
    end: timeToMinutes(end),
  };
}

function buildOpenHours(start?: string, end?: string): { start: number; end: number }[] {
  const openStart = timeToMinutes(start);
  const openEnd = timeToMinutes(end);
  if (openStart === 0 && openEnd === 0) {
    return [{ start: 0, end: 1440 }];
  }
  return [{ start: openStart, end: openEnd }];
}

function timeToMinutes(value?: string): number {
  if (!value) return 0;
  const parts = value.split(':').map((v) => Number(v));
  if (parts.length < 2) return 0;
  const [hours, minutes] = parts;
  return hours * 60 + minutes;
}

function parseTravelMinutes(value?: string): number {
  if (!value) return 0;
  const parts = value.split(':').map((segment) => Number(segment));
  if (parts.length < 2 || parts.some((segment) => !Number.isFinite(segment))) return 0;
  return (parts[0] * 60) + parts[1];
}

function ccToCuFt(value: any): number {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return 0;
  return num / 28316.8466;
}

function normalizeAvailableCapacity(source: AnyRecord | undefined): {
  avlCrates: number;
  avlKgs: number;
  avlKms: number;
  avlTp: number;
  avlVolCc: number;
} {
  const input = source ?? {};
  return {
    avlCrates: toNumber(input.avlCrates ?? input.tripAvailableCapacityCrates),
    avlKgs: toNumber(input.avlKgs ?? input.tripAvailableCapacityKgs),
    avlKms: toNumber(input.avlKms ?? input.tripAvailableCapacityKms),
    avlTp: toNumber(input.avlTp ?? input.tripAvailableCapacityTp),
    avlVolCc: toNumber(input.avlVolCc ?? input.tripAvailableCapacityVolCc),
  };
}

function normalizeSlotTags(source: unknown): string[] {
  if (Array.isArray(source)) {
    return source.map((value) => String(value).trim()).filter(Boolean);
  }
  if (typeof source === 'string') {
    return source
      .split('|')
      .map((value) => value.trim())
      .filter(Boolean);
  }
  return [];
}

function normalizeStringArray(source: unknown): string[] {
  if (Array.isArray(source)) {
    return source.map((value) => String(value).trim()).filter(Boolean);
  }
  if (typeof source === 'string') {
    return source
      .split('|')
      .map((value) => value.trim())
      .filter(Boolean);
  }
  return [];
}

function toNumber(value: any): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function mapVerifyDriverToInfo(item: AnyRecord): DriverInfo {
  return {
    driverId: item.driverId ?? '',
    name: item.driverName ?? '',
    class: item.vendorName ?? '',
    available: true,
    vehicleCapacityKg: toNumber(item.vehicleWeightCapacityKg),
    allowedVehicleTypes: item.vehicleType ? [item.vehicleType] : [],
    licenceClasses: [],
    skillTags: [],
  };
}

function hasGeoCorrectedTag(value: string): boolean {
  return value.includes(GEO_CORRECTED_TAG);
}

function stripGeoCorrectedTag(value: string): string {
  return value
    .replace(GEO_CORRECTED_TAG, '')
    .replace(LOW_CONFIDENCE_GEO_TAG, '')
    .replace(DERIVED_GEO_TAG, '')
    .trim();
}

function resolveGeoStatus(value: string): 'original' | 'derived' | 'low_confidence' | 'corrected' {
  if (hasGeoCorrectedTag(value)) return 'corrected';
  if (value.includes(LOW_CONFIDENCE_GEO_TAG)) return 'low_confidence';
  if (value.includes(DERIVED_GEO_TAG)) return 'derived';
  return 'original';
}

function mapVerifyDriverToFleet(item: AnyRecord): VehicleInfo {
  const vehicleId = String(item.vehicleNumber ?? item.driverId ?? '');
  return {
    vehicleId,
    name: vehicleId,
    capacity: toNumber(item.vehicleWeightCapacityKg),
    uom: 'kg',
    itemType: 'delivery',
    licenseRequired: '',
    assetType: item.vehicleType || 'TRUCK',
    tractorNeeded: 'No',
  };
}
